import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models, Embed, Report, Page } from 'powerbi-client';
import { ApFooter, snack } from '@alixpartners/ui-components';
import { useAppDispatch, useAppSelector } from '../../../state';
import { ReportLanding } from '../../../components/ReportLanding';
import {
  fetchSettings,
  selectSettings,
} from '../../../state/slices/settingsSlice';
import {
  fetchPowerBiReports,
  selectPowerBiReports,
} from '../../../state/slices/powerBiReportsSlice';
import {
  getEmbedData,
  selectPowerBiEmbedState,
} from '../../../state/slices/powerBiEmbedSlice';
import {
  Container,
  PageLoading,
  EmptyPlaceholder,
  SecondaryNavigation,
  ButtonTab,
  ReportWrapper,
  ScrollableContent,
  Overlay,
} from './ClientReport.styles';
import { getPowerBiEmbedData } from '../../../utils/api';
import { IReport, IReportPage } from '../../../types';

import { ClientReportHeader } from '../ClientReportHeader';
import { ErrorModal } from '../../../components/ErrorModal';

const TOKEN_TYPES = {
  Aad: models.TokenType.Aad,
  Embed: models.TokenType.Embed,
};

export const ClientReport: React.FC<{}> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { reportId, pageId } = useParams();
  const reportObject = useRef<Report | null>(null);

  const [currentReport, setCurrentReport] = useState<IReport>();
  const [reportPages, setReportPages] = useState<Page[]>([]);
  const [currentPageName, setCurrentPageName] = useState<string>();
  const [currentPageHeight, setCurrentPageHeight] = useState<number>();
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [isFiltersShowing, setIsFiltersShowing] = useState<boolean>(false);
  const [isBookmarkShowing, setIsBookmarkShowing] = useState<boolean>(false);
  const [isShowingLanding, setIsShowingLanding] = useState<boolean>(true);
  const [showErrorModal, setShowErrorModal] = useState('');

  const { settings, loading: isFetchingSettings } =
    useAppSelector(selectSettings);
  const { sharedReports, loading: isFetchingReports } =
    useAppSelector(selectPowerBiReports);
  const { embedData, isLoading: isLoadingEmbedData } = useAppSelector(
    selectPowerBiEmbedState
  );

  const isPageLoading = isFetchingReports || isFetchingSettings;

  useEffect(() => {
    dispatch(fetchSettings());
    dispatch(fetchPowerBiReports());
  }, []);

  useEffect(() => {
    if (!isFetchingReports && sharedReports!.length > 0) {
      const report =
        sharedReports?.find((r) => r.id === reportId) || sharedReports![0];
      const page =
        report.reportPages.find((p) => p.id === pageId) ||
        report.reportPages[0];

      handleProcessReport(report, page);
    }
  }, [isFetchingReports, reportId]);

  const handleProcessReport = (report: IReport, page: IReportPage) => {
    const groupId = report.reportWebUrl.split('/')[4];

    if (reportId) {
      setIsReportLoading(true);
      setCurrentReport(report);
      setCurrentPageName(page.id);
      setCurrentPageHeight(undefined);

      dispatch(getEmbedData({ reportId: report.id, groupId }));

      snack.success({
        message:
          'Report has been loaded succesfully, rendering to the screen...',
        position: 'bottom-right',
      });

      navigate(`../${report.id}/${page.id}`);
    } else {
      // Only update url when we hit /client-report-site without report id
      navigate(`${report.id}/${page.id}`);
    }
  };

  const handlePageClick = (newPage: Page) => {
    if (newPage.name !== currentPageName) {
      const page: Page = reportPages.find((p) => p.name === newPage.name)!;
      setCurrentPageName(page.name);
      setCurrentPageHeight(page.defaultSize.height);
      navigate(`../${currentReport!.id}/${page.name}`);
      page.setActive();
    }
  };

  const handleTokenRefresh = async () => {
    console.log('DEBUG: handleTokenRefresh called', Date());
    const groupId = currentReport!.reportWebUrl.split('/')[4];
    let errorDescription =
      'Sorry, there was an error while refreshing the auth token, used for loading embed report. Please refresh the page to retry. Errom message:';
    console.log('DEBUG: refreshing token', Date());

    let maxRetries = 5;
    while (maxRetries > 0) {
      try {
        const response = await getPowerBiEmbedData(currentReport!.id, groupId);
        console.log('DEBUG: received response ', response, Date());
        console.log(
          'DEBUG: received token ',
          response.embedToken.token,
          Date()
        );

        return response.embedToken.token as string;
      } catch (error) {
        console.log('DEBUG: error refreshing token', error);
        try {
          errorDescription += ` (${error} ${JSON.stringify(error)})`;
        } catch {}
        maxRetries--;
      }
    }

    // display error errorDescription
    setShowErrorModal(errorDescription);
  };

  const handleEnterFullscreen = () => {
    document.getElementById('ap-report-container')!.requestFullscreen();
  };

  // Set the filter panel
  const handleFilters = () => {
    setIsFiltersShowing(!isFiltersShowing);
  };

  // Set the bookmarks panel
  const handleBookmarks = () => {
    setIsBookmarkShowing(!isBookmarkShowing);
  };

  const handleReportLoaded = async () => {
    const reportPages = await reportObject.current!.getPages();
    const currentTab = reportPages.find((p) => p.name === currentPageName);

    if (currentTab) {
      currentTab.setActive();
      setCurrentPageHeight(currentTab.defaultSize.height);
    }

    setReportPages(reportPages);
    setIsReportLoading(false);
  };

  const handleReportError = (error: any) => {
    const message = error?.detail
      ? error.detail.detailedMessage
      : 'Sorry, there was an error while loading embed report';

    snack.error({
      message,
      position: 'bottom-right',
    });
  };

  const handleReportEmbedComponent = (embedObject: Embed) => {
    reportObject.current = embedObject as Report;
  };

  // TO DO
  // Move this to a router approach while retaining background loading
  if (isShowingLanding) {
    return (
      <>
        <ClientReportHeader
          sharedReports={sharedReports}
          clientName={settings?.clientName}
          fullscreenButtonDisabled={isReportLoading}
          bookmarksDisabled={isReportLoading}
          onFullscreenClick={handleEnterFullscreen}
          onBookmarksClick={handleBookmarks}
          bookmarksShowing={isBookmarkShowing}
          obscure={true}
          onFiltersClick={handleFilters}
          filtersDisabled={isReportLoading}
          filtersShowing={isFiltersShowing}
        />
        <ReportLanding
          showLoading={isPageLoading}
          setLandingPage={() => setIsShowingLanding(false)}
        />
      </>
    );
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  return (
    <>
      <ClientReportHeader
        sharedReports={sharedReports}
        clientName={settings?.clientName}
        fullscreenButtonDisabled={isReportLoading}
        bookmarksDisabled={isReportLoading}
        onFullscreenClick={handleEnterFullscreen}
        onBookmarksClick={handleBookmarks}
        bookmarksShowing={isBookmarkShowing}
        onFiltersClick={handleFilters}
        filtersDisabled={isReportLoading}
        filtersShowing={isFiltersShowing}
      />
      <Container id="ap-report-container">
        {sharedReports && sharedReports?.length > 0 ? (
          <>
            <SecondaryNavigation>
              {reportPages
                .filter((page) => !page.visibility)
                .map((page) => (
                  <ButtonTab
                    key={page.name}
                    isSelected={page.name === currentPageName}
                    onClick={() => handlePageClick(page)}
                    disabled={isReportLoading}
                    theme="light"
                  >
                    {page.displayName}
                  </ButtonTab>
                ))}
            </SecondaryNavigation>
            <ScrollableContent>
              {isReportLoading && <Overlay />}
              {!isLoadingEmbedData && embedData ? (
                <ReportWrapper pageHeight={currentPageHeight}>
                  <PowerBIEmbed
                    embedConfig={{
                      type: 'report',
                      id: embedData.id,
                      embedUrl: embedData.embedUrl,
                      accessToken: embedData?.embedToken?.token,
                      tokenType:
                        TOKEN_TYPES[
                          embedData.tokenType as keyof typeof TOKEN_TYPES
                        ],
                      eventHooks: {
                        accessTokenProvider: handleTokenRefresh,
                      },
                      settings: {
                        layoutType: models.LayoutType.Custom,
                        customLayout: {
                          displayOption: models.DisplayOption.ActualSize,
                        },
                        panes: {
                          filters: {
                            visible: isFiltersShowing,
                          },
                          pageNavigation: {
                            visible: false,
                          },
                          bookmarks: {
                            visible: isBookmarkShowing,
                          },
                        },
                        personalBookmarksEnabled: true,
                        persistentFiltersEnabled: true,
                        bars: {
                          actionBar: {
                            visible: false,
                          },
                        },
                      },
                    }}
                    eventHandlers={
                      new Map([
                        ['loaded', handleReportLoaded],
                        ['error', handleReportError],
                      ])
                    }
                    cssClassName={'ap-report-wrapper'}
                    getEmbeddedComponent={handleReportEmbedComponent}
                  />
                </ReportWrapper>
              ) : null}
              {!isReportLoading && <ApFooter />}
            </ScrollableContent>
          </>
        ) : (
          <>
            <EmptyPlaceholder />
            <ApFooter />
          </>
        )}
        <ErrorModal
          isOpen={!!showErrorModal}
          header="Error while refreshing the auth token for embed report"
          onModalClose={() => {
            setShowErrorModal('');
          }}
          onRetry={() => {
            // refresh the page
            window.location.reload();
          }}
          errorText={showErrorModal}
        />
      </Container>
    </>
  );
};
